/**
 * RulesSection - state-check list inside the state dialog's Rules tab.
 *
 * Lists the ENTRY and EXIT checks attached to the given state, grouped by
 * stage and ordered by priority (high -> low), and exposes add / edit /
 * delete. ENTRY checks gate entry into this state (MOVE / block); EXIT
 * checks gate leaving it and can DIVERT a user action to another action.
 * Creation is disabled until the parent state has been saved - checks are
 * keyed on the state UUID.
 */

import { useMemo, useState } from 'react'
import { Pencil, Plus, Trash2 } from 'lucide-react'
import { toast } from 'sonner'

import { Badge } from '@/shared/components/ui/badge'
import { Button } from '@/shared/components/ui/button'
import { ConfirmationDialog } from '@/admin/components/shared/confirmation-dialog'
import {
  useDeleteStateCheck,
  useWorkflowStateChecks,
  type WorkflowStateCheck,
  type WorkflowStateAdmin,
} from '@/admin/hooks/use-workflow-definitions'
import { toastApiError } from '@/shared/lib/api-error'

import { RuleDialog } from './rule-dialog'

interface RulesSectionProps {
  stateUuid: string | undefined
  workflowUuid: string
  workflowKey: string
  resourceType?: string
  states: WorkflowStateAdmin[]
}

export function RulesSection({
  stateUuid,
  workflowUuid,
  workflowKey,
  resourceType,
  states,
}: RulesSectionProps) {
  const [editing, setEditing] = useState<WorkflowStateCheck | null>(null)
  const [showCreate, setShowCreate] = useState(false)
  const [deleting, setDeleting] = useState<WorkflowStateCheck | null>(null)
  const { data, isLoading } = useWorkflowStateChecks(stateUuid)
  const remove = useDeleteStateCheck(stateUuid)

  const stateByUuid = useMemo(() => {
    const map = new Map<string, WorkflowStateAdmin>()
    for (const s of states) map.set(s.uuid, s)
    return map
  }, [states])

  const sorted = useMemo(
    () =>
      [...(data ?? [])].sort(
        (a, b) => b.priority - a.priority || a.name.localeCompare(b.name),
      ),
    [data],
  )
  const entryChecks = useMemo(
    () => sorted.filter((c) => c.stage !== 'EXIT'),
    [sorted],
  )
  const exitChecks = useMemo(
    () => sorted.filter((c) => c.stage === 'EXIT'),
    [sorted],
  )

  if (!stateUuid) {
    return (
      <div className="rounded-[6px] border-[0.5px] border-dashed border-[var(--rule)] bg-[var(--bg-subtle)] px-3 py-5 text-center text-[11px] text-[var(--ink-3)]">
        Save the state first - state checks attach once the state has a UUID.
      </div>
    )
  }

  async function handleDelete() {
    if (!deleting) return
    try {
      await remove.mutateAsync(deleting.uuid)
      toast.success('Check deleted')
      setDeleting(null)
    } catch (err) {
      toastApiError(err, 'Failed to delete check')
    }
  }

  const renderGroup = (
    title: string,
    hint: string,
    checks: WorkflowStateCheck[],
    emptyLabel: string,
  ) => (
    <div className="flex flex-col gap-1.5">
      <div className="flex flex-col">
        <h4 className="text-[11px] font-medium uppercase tracking-[0.12em] text-[var(--ink-2)]">
          {title}
        </h4>
        <p className="text-[10px] text-[var(--ink-3)]">{hint}</p>
      </div>
      {checks.length === 0 ? (
        <div className="rounded-[6px] border-[0.5px] border-dashed border-[var(--rule)] bg-[var(--bg-subtle)] px-3 py-3 text-center text-[11px] text-[var(--ink-3)]">
          {emptyLabel}
        </div>
      ) : (
        <div className="overflow-hidden rounded-[6px] border-[0.5px] border-[var(--rule)]">
          {checks.map((rule) => (
            <RuleRow
              key={rule.uuid}
              rule={rule}
              targetLabel={
                rule.target_state
                  ? (stateByUuid.get(rule.target_state)?.label ?? '-')
                  : null
              }
              onEdit={() => setEditing(rule)}
              onDelete={() => setDeleting(rule)}
            />
          ))}
        </div>
      )}
    </div>
  )

  return (
    <section className="flex flex-col gap-3">
      <div className="flex items-center justify-between">
        <div className="flex flex-col">
          <h3 className="text-[12px] font-medium uppercase tracking-[0.12em] text-[var(--ink-2)]">
            State checks
          </h3>
          <p className="text-[10px] text-[var(--ink-3)]">
            Entry checks gate arriving at this state; exit checks gate leaving
            it and can divert an action.
          </p>
        </div>
        <Button
          variant="outline"
          size="xs"
          onClick={() => setShowCreate(true)}
          data-testid="workflows-rules-add"
        >
          <Plus size={11} className="mr-1" />
          Add check
        </Button>
      </div>

      {isLoading ? (
        <div className="rounded-[6px] border-[0.5px] border-dashed border-[var(--rule)] bg-[var(--bg-subtle)] px-3 py-4 text-center text-[11px] text-[var(--ink-3)]">
          Loading checks…
        </div>
      ) : (
        <>
          {renderGroup(
            'Entry checks',
            'Evaluated on entry. Matching checks raise issues and can redirect the instance to another state.',
            entryChecks,
            'No entry checks yet.',
          )}
          {renderGroup(
            'Exit checks',
            'Evaluated as a user action leaves this state. A divert check blocks the action and runs another action instead.',
            exitChecks,
            'No exit checks yet.',
          )}
        </>
      )}

      <RuleDialog
        open={showCreate}
        onOpenChange={setShowCreate}
        mode="create"
        stateUuid={stateUuid}
        workflowUuid={workflowUuid}
        workflowKey={workflowKey}
        resourceType={resourceType}
        states={states}
      />
      <RuleDialog
        open={!!editing}
        onOpenChange={(next) => {
          if (!next) setEditing(null)
        }}
        mode="edit"
        stateUuid={stateUuid}
        workflowUuid={workflowUuid}
        workflowKey={workflowKey}
        resourceType={resourceType}
        states={states}
        rule={editing ?? undefined}
      />
      <ConfirmationDialog
        open={!!deleting}
        onOpenChange={(next) => {
          if (!next) setDeleting(null)
        }}
        title={`Delete ${deleting?.name ?? 'check'}?`}
        description="This removes the check from the workflow state. Future evaluations will no longer apply it."
        confirmLabel="Delete"
        pendingLabel="Deleting..."
        destructive
        isPending={remove.isPending}
        onConfirm={handleDelete}
      />
    </section>
  )
}

interface RuleRowProps {
  rule: WorkflowStateCheck
  targetLabel: string | null
  onEdit: () => void
  onDelete: () => void
}

function RuleRow({ rule, targetLabel, onEdit, onDelete }: RuleRowProps) {
  return (
    <div
      className="flex items-center gap-2 border-t-[0.5px] border-t-[var(--rule)] bg-[var(--bg)] px-3 py-2 first:border-t-0"
      data-testid={`workflows-rule-row-${rule.uuid}`}
    >
      <div className="flex min-w-0 flex-1 flex-col gap-1">
        <div className="flex min-w-0 items-center gap-2">
          <span className="truncate text-[12px] text-[var(--ink)]">
            {rule.name}
          </span>
          <SeverityBadge severity={rule.severity} />
          <OutcomeBadge
            outcome={rule.outcome}
            targetLabel={targetLabel}
          />
          {!rule.is_active && (
            <Badge variant="outline" className="text-[10px]">
              inactive
            </Badge>
          )}
          <span className="text-[10px] text-[var(--ink-3)]">
            p{rule.priority}
          </span>
        </div>
        {rule.description && (
          <span className="truncate text-[10px] text-[var(--ink-3)]">
            {rule.description}
          </span>
        )}
      </div>
      <div className="flex shrink-0 items-center gap-1">
        <Button variant="ghost" size="icon-xs" onClick={onEdit} title="Edit check">
          <Pencil size={13} />
        </Button>
        <Button
          variant="ghost"
          size="icon-xs"
          onClick={onDelete}
          title="Delete check"
        >
          <Trash2 size={13} />
        </Button>
      </div>
    </div>
  )
}

function SeverityBadge({
  severity,
}: {
  severity: WorkflowStateCheck['severity']
}) {
  const map = {
    BLOCK: { label: 'block', variant: 'destructive' as const },
    WARN: { label: 'warn', variant: 'secondary' as const },
    INFO: { label: 'info', variant: 'outline' as const },
  }
  const cfg = map[severity]
  return (
    <Badge
      variant={cfg.variant}
      className="text-[10px] uppercase tracking-[0.08em]"
    >
      {cfg.label}
    </Badge>
  )
}

function OutcomeBadge({
  outcome,
  targetLabel,
}: {
  outcome: WorkflowStateCheck['outcome']
  targetLabel: string | null
}) {
  if (outcome === 'MOVE') {
    return (
      <Badge variant="secondary" className="text-[10px]">
        → {targetLabel ?? 'state'}
      </Badge>
    )
  }
  if (outcome === 'DIVERT') {
    return (
      <Badge variant="secondary" className="text-[10px]">
        divert action
      </Badge>
    )
  }
  return (
    <Badge variant="outline" className="text-[10px]">
      no-move
    </Badge>
  )
}
